import type { ReactNode } from "react";
import type { Ampel as AmpelType, LicenseClass } from "../../domain/types";
import { CLASS_LABEL } from "../../domain/rules";

export function Card({ title, subtitle, right, children, className = "" }: {
  title?: ReactNode; subtitle?: ReactNode; right?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={`bg-card shadow-card rounded-xl border-soft ${className}`} style={{ borderWidth: 1 }}>
      {(title || right) && (
        <header className="flex items-start justify-between gap-4 px-5 pt-4 pb-3">
          <div>
            {title && <h3 className="text-strong text-[15px] font-semibold">{title}</h3>}
            {subtitle && <p className="text-dim mt-0.5 text-sm">{subtitle}</p>}
          </div>
          {right && <div className="shrink-0">{right}</div>}
        </header>
      )}
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}

type Tone = "neutral" | "brand" | "green" | "amber" | "red";
const toneColor: Record<Tone, string | undefined> = {
  neutral: undefined, brand: "var(--color-brand-600)", green: "var(--color-amp-green)",
  amber: "var(--color-amp-amber)", red: "var(--color-amp-red)",
};

export function KpiCard({ label, value, sub, tone = "neutral", big }: {
  label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: Tone; big?: boolean;
}) {
  return (
    <div className="bg-card shadow-card flex h-full flex-col rounded-xl border-soft px-5 py-4" style={{ borderWidth: 1 }}>
      <div className="text-dim text-[13px] font-medium tracking-wide uppercase">{label}</div>
      <div className={`tnum mt-1.5 font-semibold ${big ? "text-4xl" : "text-2xl"}`}
        style={{ color: toneColor[tone] ?? "var(--text-strong)" }}>
        {value}
      </div>
      {sub && <div className="text-dim mt-1 text-sm">{sub}</div>}
    </div>
  );
}

const ampColor: Record<AmpelType, string> = {
  gruen: "var(--color-amp-green)", gelb: "var(--color-amp-amber)",
  rot: "var(--color-amp-red)", grau: "var(--color-amp-gray)",
};
export function Ampel({ state, size = 12 }: { state: AmpelType; size?: number }) {
  return <span className="inline-block shrink-0 rounded-full" style={{ width: size, height: size, backgroundColor: ampColor[state] }} aria-hidden />;
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: Tone | "gray" | "outline" }) {
  const styleMap: Record<string, React.CSSProperties> = {
    neutral: { backgroundColor: "var(--surface-sunken)", color: "var(--text-dim)" },
    brand: { backgroundColor: "var(--color-brand-600)", color: "#fff" },
    green: { backgroundColor: "var(--color-amp-green)", color: "#fff" },
    amber: { backgroundColor: "var(--color-amp-amber)", color: "#fff" },
    red: { backgroundColor: "var(--color-amp-red)", color: "#fff" },
    gray: { backgroundColor: "var(--surface-sunken)", color: "var(--text-faint)" },
    outline: { borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" },
  };
  return <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium" style={styleMap[tone]}>{children}</span>;
}

// ------------------------------------------------------------ Lizenzklassen — EINE Farblogik für alle Seiten
export type KlasseTone = Tone | "gray";
/**
 * Farbe je Lizenzklasse, überall gleich:
 *  · Developer  rot    (× 2 gewichtet — die teuerste Klasse)
 *  · Advanced   amber  (voller FUE)
 *  · Core       brand  (eigene, gut sichtbare Farbe — die Zielklasse der meisten Maßnahmen)
 *  · Self-Service / nicht relevant  grau/neutral
 */
export function classTone(cls: LicenseClass): KlasseTone {
  switch (cls) {
    case "Developer": return "red";
    case "Advanced": return "amber";
    case "Core": return "brand";
    case "SelfService": return "neutral";
    default: return "gray";
  }
}

/** Lizenzklasse als Badge in ihrer Farbe. `outline` für nicht tragende Klassen (informativ). */
export function KlasseBadge({ cls, outline = false, children }: { cls: LicenseClass; outline?: boolean; children?: ReactNode }) {
  return <Badge tone={outline ? "outline" : classTone(cls)}>{children ?? CLASS_LABEL[cls]}</Badge>;
}

export function BeispieldatenTag() {
  return (
    <span className="text-faint inline-flex items-center gap-1 text-xs">
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "var(--color-amp-amber)" }} />
      Beispieldaten · kein Produktivsystem
    </span>
  );
}
